import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Header } from "@/components/Header";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { InputModeSelector } from "@/components/InputModeSelector";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { SELECTOR_STEPS, BUILDER_STEPS } from "@/components/onboarding/tourSteps";
import { useOnboardingTour } from "@/hooks/useOnboardingTour";
import { TailorResumeFlow } from "@/components/TailorResumeFlow";
import { ResumeForm } from "@/components/ResumeForm";
import { ResumePreview } from "@/components/ResumePreview";
import { ResumePreviewHarvard } from "@/components/ResumePreviewHarvard";
import { ATSScoreCard } from "@/components/ATSScoreCard";
import { JobDescriptionInput } from "@/components/JobDescriptionInput";
import { ExportButtons } from "@/components/ExportButtons";
import { SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";
import { ATSScore, ResumeAnalysis, ResumeData, ResumeFormat, ResumeStyle } from "@/types/resume";
import { createEmptyResume, generatePlainTextResume, generateExportFileNameFromInputs } from "@/lib/resumeUtils";
import { analyzeImportedResume, compressResumeToOnePage } from "@/lib/aiOptimization";
import { normalizeATSScore } from "@/lib/atsScoreUtils";
import { generateDOCX, generatePDF } from "@/lib/documentExport";
import { generateHarvardPDF, generateHarvardDOCX } from "@/lib/harvardExport";
import { extractResumeTextFromFile } from "@/lib/resumeImport";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Save, Minimize2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExportNamingFields } from "@/components/ExportNamingFields";
import { useExportNamingPrefs } from "@/hooks/useExportNamingPrefs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocation, useNavigate } from "react-router-dom";

const Index = () => {
  const { user } = useAuth();
  const naming = useExportNamingPrefs();
  const [mode, setMode] = useState<"select" | "form" | "tailor">("select");
  const [resumeData, setResumeData] = useState<ResumeData>(createEmptyResume());
  const [jobDescription, setJobDescription] = useState("");
  const [extractedKeywords, setExtractedKeywords] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [resumeFormat, setResumeFormat] = useState<ResumeFormat>("standard");
  const [resumeStyle, setResumeStyle] = useState<ResumeStyle>("modern");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [resumeName, setResumeName] = useState("");
  const [atsScore, setAtsScore] = useState<ATSScore | null>(null);
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(defaultSectionVisibility);
  const [sectionOrder, setSectionOrder] = useState<SectionKey[]>(DEFAULT_SECTION_ORDER);
  const location = useLocation();
  const navigate = useNavigate();
  const fullTourReplay = useMemo(
    () => new URLSearchParams(location.search).get("tour") === "full",
    [location.search]
  );
  const fullReplayStartedRef = useRef(false);
  const startBuilderAfterReplayRef = useRef(false);

  // Navigation guard - block when user has content in form mode
  const hasUnsavedWork = mode === "form" && (
    !!resumeData.personalInfo.fullName ||
    resumeData.experience.length > 0 ||
    resumeData.education.length > 0 ||
    !!resumeData.summary
  );
  useNavigationGuard(hasUnsavedWork);

  const exportFileName = generateExportFileNameFromInputs({
    role: naming.role,
    company: naming.company,
    applicantFullName: naming.applicantName || resumeData.personalInfo.fullName,
  });

  const previewScale = 0.55;
  const isFirefox = typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);
  const previewWrapperStyle: React.CSSProperties = isFirefox
    ? {
        transform: `scale(${previewScale})`,
        transformOrigin: "top center",
        width: `${612 / previewScale}px`,
      }
    : ({
        zoom: previewScale,
        width: 612,
      } as React.CSSProperties);

  const handleSelectMode = (selectedMode: "form" | "paste" | "tailor") => {
    if (selectedMode === "form") {
      setMode("form");
    } else if (selectedMode === "tailor") {
      setMode("tailor");
    }
  };

  const handleImportResume = async (file: File) => {
    setIsAnalyzing(true);
    try {
      const rawText = await extractResumeTextFromFile(file);
      if (!rawText || rawText.trim().length < 50) {
        throw new Error("We couldn't extract enough text from that file. Try a different PDF/DOCX, or upload a TXT version.");
      }
      await processResumeText(rawText);
      setMode("form");
      setAnalysis(null);
      toast.success("Resume imported and analyzed.");
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to import resume. Please try a different file.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleImportText = async (text: string) => {
    setIsAnalyzing(true);
    try {
      await processResumeText(text);
      setMode("form");
      setAnalysis(null);
      toast.success("Resume text parsed and analyzed.");
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to parse resume text.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const processResumeText = async (rawText: string) => {
    const result = await analyzeImportedResume(rawText);
    setResumeData(result.parsedResumeData);
    setAnalysis(result.analysis);
    if (result.atsScore) {
      setAtsScore(normalizeATSScore(result.atsScore, result.parsedResumeData, { hasTargetJob: false }));
    }
    if (result.extractedKeywords) {
      setExtractedKeywords(result.extractedKeywords);
    }
  };

  const handleTailorComplete = (data: ResumeData, score: ATSScore, keywords: string[]) => {
    setResumeData(data);
    if (score && score.overall > 0) {
      setAtsScore(normalizeATSScore(score, data, { hasTargetJob: true }));
    }
    setExtractedKeywords(keywords);
    setMode("form");
  };

  const handleExportPDF = (fileName?: string) => {
    if (!resumeData.personalInfo.fullName) {
      toast.error("Please add your name before exporting");
      return;
    }
    try {
      if (resumeStyle === "harvard") {
        generateHarvardPDF(resumeData, resumeFormat, fileName, sectionVisibility, sectionOrder);
      } else {
        generatePDF(resumeData, resumeFormat, fileName, sectionVisibility, sectionOrder);
      }
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleExportDOCX = async (fileName?: string) => {
    if (!resumeData.personalInfo.fullName) {
      toast.error("Please add your name before exporting");
      return;
    }
    try {
      if (resumeStyle === "harvard") {
        await generateHarvardDOCX(resumeData, resumeFormat, fileName, sectionVisibility, sectionOrder);
      } else {
        await generateDOCX(resumeData, resumeFormat, fileName, sectionVisibility, sectionOrder);
      }
      toast.success("DOCX downloaded successfully!");
    } catch (error) {
      console.error("DOCX export error:", error);
      toast.error("Failed to generate DOCX");
    }
  };

  const handleCopyText = () => {
    const text = generatePlainTextResume(resumeData);
    navigator.clipboard.writeText(text);
    toast.success("Resume copied to clipboard!");
  };

  const handleJobDescriptionChange = (value: string) => {
    setJobDescription(value);
    if (value.length > 50) {
      const commonKeywords = value
        .toLowerCase()
        .match(
          /\b(python|javascript|react|sql|aws|agile|communication|leadership|project management|data analysis|machine learning|excel|tableau|salesforce|marketing|sales|operations|customer service|teamwork|problem.solving)\b/gi
        );
      if (commonKeywords) {
        setExtractedKeywords([...new Set(commonKeywords.map((k) => k.toLowerCase()))]);
      }
    } else {
      setExtractedKeywords([]);
    }
  };

  const handleCompressToOnePage = async () => {
    if (!resumeData.personalInfo.fullName) {
      toast.error("Please add content before compressing");
      return;
    }
    setIsCompressing(true);
    try {
      const compressed = await compressResumeToOnePage(resumeData);
      setResumeData(compressed);
      toast.success("Resume compressed to fit one page!");
    } catch (error) {
      console.error("Compression error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to compress resume");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSaveResume = async () => {
    if (!resumeData.personalInfo.fullName) {
      toast.error("Please add your name before saving");
      return;
    }
    if (!resumeName.trim()) {
      toast.error("Please enter a name for this resume");
      return;
    }
    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await supabase.from("saved_resumes").insert([
        {
          user_id: user?.id as string,
          name: resumeName.trim(),
          resume_data: resumeData as any,
          ats_score: atsScore?.overall ?? null,
          target_job: jobDescription ? jobDescription.slice(0, 200) : null,
        },
      ]);
      if (error) throw error;
      toast.success("Resume saved successfully!");
      setSaveDialogOpen(false);
      setResumeName("");
    } catch (error) {
      console.error("Error saving resume:", error);
      toast.error("Failed to save resume");
    } finally {
      setIsSaving(false);
    }
  };

  const selectorTour = useOnboardingTour("selector");
  const builderTour = useOnboardingTour("builder");

  useEffect(() => {
    if (!fullTourReplay || mode !== "select" || fullReplayStartedRef.current) return;
    fullReplayStartedRef.current = true;
    selectorTour.startTour();
    navigate("/create", { replace: true });
  }, [fullTourReplay, mode, selectorTour.startTour, navigate]);

  useEffect(() => {
    if (mode !== "form" || !startBuilderAfterReplayRef.current) return;

    const timer = setTimeout(() => {
      builderTour.startTour();
      startBuilderAfterReplayRef.current = false;
    }, 250);

    return () => clearTimeout(timer);
  }, [mode, builderTour.startTour]);

  const handleSelectorTourFinish = useCallback(() => {
    selectorTour.finish();
    if (fullReplayStartedRef.current) {
      startBuilderAfterReplayRef.current = true;
      setMode("form");
    }
  }, [selectorTour.finish]);

  if (mode === "select") {
    return (
      <div className="min-h-screen gradient-surface">
        <Header />
        <InputModeSelector
          onSelectMode={handleSelectMode}
          onImportResume={handleImportResume}
          onImportText={handleImportText}
          isAnalyzing={isAnalyzing}
          analysis={analysis}
        />
        <OnboardingTour
          steps={SELECTOR_STEPS}
          currentStep={selectorTour.currentStep}
          isActive={selectorTour.isActive}
          onNext={selectorTour.next}
          onPrev={selectorTour.prev}
          onSkip={selectorTour.skip}
          onFinish={handleSelectorTourFinish}
        />
      </div>
    );
  }

  if (mode === "tailor") {
    return (
      <div className="min-h-screen gradient-surface">
        <Header />
        <TailorResumeFlow onComplete={handleTailorComplete} onBack={() => setMode("select")} />
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-surface">
      <Header />

      <main className="w-full px-4 sm:px-6 lg:px-8 py-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setMode("select");
              setAnalysis(null);
            }}
            className="gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          <div className="flex items-center gap-2 flex-wrap">
            {/* File naming */}
            <div data-tour="file-naming">
              <ExportNamingFields
                role={naming.role}
                company={naming.company}
                applicantName={naming.applicantName}
                onRoleChange={naming.setRole}
                onCompanyChange={naming.setCompany}
                onApplicantNameChange={naming.setApplicantName}
              />
            </div>

            {/* Template & format selectors */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select value={resumeStyle} onValueChange={(v) => setResumeStyle(v as ResumeStyle)}>
                    <SelectTrigger className="w-[110px] h-9">
                      <SelectValue placeholder="Style" />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="harvard">Harvard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              <TooltipContent>Resume template style</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select value={resumeFormat} onValueChange={(v) => setResumeFormat(v as ResumeFormat)}>
                    <SelectTrigger className="w-[110px] h-9">
                      <SelectValue placeholder="Format" />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="compact">Compact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              <TooltipContent>Spacing density</TooltipContent>
            </Tooltip>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="lg:hidden gap-1.5"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? "Hide" : "Preview"}
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCompressToOnePage}
                  disabled={!resumeData.personalInfo.fullName || isCompressing}
                >
                  <Minimize2 className="w-4 h-4 mr-1.5" />
                  {isCompressing ? "..." : "1 Page"}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Compress resume to fit one page</TooltipContent>
            </Tooltip>

            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button data-tour="save-btn" variant="secondary" size="sm" disabled={!resumeData.personalInfo.fullName}>
                  <Save className="w-4 h-4 mr-1.5" />
                  Save
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Save Resume</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Resume Name</Label>
                    <Input
                      placeholder="e.g., Software Engineer Resume"
                      value={resumeName}
                      onChange={(e) => setResumeName(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleSaveResume} disabled={isSaving} className="w-full">
                    {isSaving ? "Saving..." : "Save Resume"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div data-tour="export-btns">
              <ExportButtons
                onExportPDF={handleExportPDF}
                onExportDOCX={handleExportDOCX}
                onCopyText={handleCopyText}
                defaultFileName={exportFileName}
                disabled={!resumeData.personalInfo.fullName}
              />
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="max-h-[calc(100vh-120px)] overflow-y-auto custom-scrollbar-center">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <JobDescriptionInput
                jobDescription={jobDescription}
                onJobDescriptionChange={handleJobDescriptionChange}
                extractedKeywords={extractedKeywords}
              />

              <ResumeForm
                data={resumeData}
                onChange={setResumeData}
                sectionVisibility={sectionVisibility}
                onSectionVisibilityChange={setSectionVisibility}
                sectionOrder={sectionOrder}
                onSectionOrderChange={setSectionOrder}
              />

              {/* Bottom Export Buttons */}
              <Card className="p-4 bg-card border-border">
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                  <p className="text-sm text-muted-foreground">Ready to download your resume?</p>
                  <ExportButtons
                    onExportPDF={handleExportPDF}
                    onExportDOCX={handleExportDOCX}
                    onCopyText={handleCopyText}
                    defaultFileName={exportFileName}
                    disabled={!resumeData.personalInfo.fullName}
                  />
                </div>
              </Card>

              {/* Feedback Section */}
              <Card className="p-4 bg-card border-border border-dashed">
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Facing any issues? Let us know so we can improve.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => (window.location.href = "/contact")}
                    className="gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Share Feedback
                  </Button>
                </div>
              </Card>
            </div>

            <div className={`space-y-6 ${!showPreview ? "hidden lg:block" : ""}`}>
              {atsScore && (
                <ATSScoreCard score={atsScore} keywordMatchEnabled={jobDescription.trim().length >= 50} />
              )}

              <div data-tour="live-preview" className="bg-muted/30 rounded-xl p-4">
                <div className="text-xs text-muted-foreground mb-3 flex items-center justify-between">
                  <span>
                    Live Preview ({resumeStyle === "harvard" ? "Harvard" : "Modern"} •{" "}
                    {resumeFormat === "standard" ? "Standard" : "Compact"})
                  </span>
                  <span>ATS-Friendly Format • 8.5" x 11"</span>
                </div>
                <div className="flex justify-center overflow-x-auto">
                  <div style={previewWrapperStyle} className="min-w-[612px]">
                    {resumeStyle === "harvard" ? (
                      <ResumePreviewHarvard
                        data={resumeData}
                        format={resumeFormat}
                        sectionVisibility={sectionVisibility}
                        sectionOrder={sectionOrder}
                      />
                    ) : (
                      <ResumePreview
                        data={resumeData}
                        format={resumeFormat}
                        sectionVisibility={sectionVisibility}
                        sectionOrder={sectionOrder}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <OnboardingTour
        steps={BUILDER_STEPS}
        currentStep={builderTour.currentStep}
        isActive={builderTour.isActive}
        onNext={builderTour.next}
        onPrev={builderTour.prev}
        onSkip={builderTour.skip}
        onFinish={builderTour.finish}
      />
    </div>
  );
};

export default Index;
